import { useNavigate } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { IconCamera, IconBook, IconMic, IconSparkle, IconLock, IconArrowRight } from '../components/Icons';

// The whole My Dyslexia Tools portfolio in one place. v1 has one tool live and
// three placeholders. The placeholders are real - they tell users what's coming
// and let them register interest, which feeds the portfolio prioritisation.
export function ToolsScreen() {
  const navigate = useNavigate();

  const tools = [
    {
      key: 'answer-assist',
      title: 'Answer Assist',
      subtitle: 'Photo a worksheet, dictate the answers, save as a PDF.',
      Icon: IconCamera,
      status: 'live' as const,
      onClick: () => navigate('/app/capture'),
    },
    {
      key: 'read-to-me',
      title: 'Read To Me',
      subtitle: 'Photo any page, listen to it read aloud with calm pacing and dyslexia-friendly highlighting.',
      Icon: IconBook,
      status: 'soon' as const,
    },
    {
      key: 'spelling-buddy',
      title: 'Spelling Buddy',
      subtitle: 'Dictate a word, see how to spell it. Phonics breakdown for any tricky word.',
      Icon: IconMic,
      status: 'soon' as const,
    },
    {
      key: 'word-bank',
      title: 'Word Bank',
      subtitle: 'Tap a word in any photo, get a friendly definition and pronunciation.',
      Icon: IconSparkle,
      status: 'soon' as const,
    },
  ];

  return (
    <AppShell title="Tools">
      <div className="px-5 pt-2">
        <section className="py-6">
          <h1 className="text-2xl font-medium text-ink">Tools</h1>
          <p className="mt-2 text-sm text-ink/60 leading-relaxed">
            One paid unlock gets you everything we ship, now and later.
          </p>
        </section>

        <ul className="flex flex-col gap-3">
          {tools.map((tool) => {
            const Icon = tool.Icon;
            const live = tool.status === 'live';
            return (
              <li key={tool.key}>
                <button
                  onClick={tool.onClick}
                  disabled={!live}
                  className="w-full flex items-start gap-3 p-4 rounded-2xl bg-white/80 border border-ink/5 text-left transition disabled:cursor-default hover:bg-white"
                >
                  <span className="w-11 h-11 rounded-2xl bg-accentSoft text-accent grid place-items-center flex-shrink-0">
                    <Icon size={22} />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-ink">{tool.title}</span>
                      {!live && (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-ink/40 bg-ink/5 px-2 py-0.5 rounded-full">
                          <IconLock size={10} /> Soon
                        </span>
                      )}
                    </span>
                    <span className="block mt-1 text-xs text-ink/55 leading-relaxed">
                      {tool.subtitle}
                    </span>
                  </span>
                  {live && (
                    <span className="text-accent self-center">
                      <IconArrowRight size={18} />
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>

        <section className="mt-8 rounded-2xl bg-accentSoft border border-accent/10 p-4 text-sm text-accent leading-relaxed">
          <p className="font-medium">Want a tool we have not built?</p>
          <p className="mt-1 text-accent/80">
            Email{' '}
            <a className="underline underline-offset-2" href="mailto:support@mydyslexiatools.com">
              support@mydyslexiatools.com
            </a>{' '}
            and tell us what would help your child. Reader requests drive the roadmap.
          </p>
        </section>
      </div>
    </AppShell>
  );
}
